import React from 'react';
import ReactDOM from 'react-dom';
import { toggleFullscreen, isFullscreen, onFullscreenChange } from '../services/fullscreen';

interface FullscreenButtonProps {
    className?: string;
}

/**
 * Floating fullscreen toggle button for the bottom-right corner.
 * Renders into #portal-root to avoid scaling issues.
 */
const FullscreenButton: React.FC<FullscreenButtonProps> = ({ className }) => {
    const [isFs, setIsFs] = React.useState(isFullscreen);

    React.useEffect(() => {
        const cleanup = onFullscreenChange(setIsFs);
        return cleanup;
    }, []);

    const handleClick = async () => {
        await toggleFullscreen();
    };

    const portalRoot = document.getElementById('portal-root');
    if (!portalRoot) return null;

    const button = (
        <>
        <style>{`
            @keyframes flashy-blink {
                0%, 100% { box-shadow: 0 0 0 0 rgba(6, 182, 212, 0.8); transform: scale(1); }
                50% { box-shadow: 0 0 35px 15px rgba(6, 182, 212, 0.7); transform: scale(1.15); border-color: #22d3ee; }
            }
            .animate-flashy-blink {
                animation: flashy-blink 1.5s infinite ease-in-out;
            }
        `}</style>
        <button
            onClick={handleClick}
            className={`fixed bottom-[calc(var(--shell-safe-bottom,0px)+16px)] right-[calc(var(--shell-safe-right,0px)+16px)] z-[9999] w-[55px] h-[55px] rounded-full 
        bg-gradient-to-br from-slate-800/90 to-slate-900/90 
        border-2 border-slate-600/50 hover:border-cyan-500/70
        shadow-lg hover:shadow-cyan-500/30
        flex items-center justify-center
        transition-all duration-300 transform hover:scale-110 active:scale-95
        backdrop-blur-sm ${!isFs ? 'animate-flashy-blink' : 'animate-smooth-pulse'}
        ${className || ''}`}
            title={isFs ? 'Exit Fullscreen' : 'Enter Fullscreen'}
            aria-label={isFs ? 'Exit Fullscreen' : 'Enter Fullscreen'}
        >
            {isFs ? (
                // Exit fullscreen icon (minimize arrows)
                <svg xmlns="http://www.w3.org/2000/svg" className="w-[28px] h-[28px] text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                </svg>
            ) : (
                // Enter fullscreen icon (expand arrows)
                <svg xmlns="http://www.w3.org/2000/svg" className="w-[28px] h-[28px] text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                </svg>
            )}
        </button>
        </>
    );

    return ReactDOM.createPortal(button, portalRoot);
};

export default FullscreenButton;
